import React from "react";
import TrustpilotStars5 from "lib/assets/trustpilot-stars-5.svg";
import TrustpilotLogo from "lib/assets/trustpilot-logo.svg";
import { useTheme } from "lib/contexts/themeContext";
import {
  TrustpilotCategoryRanking,
  TrustpilotReview,
  TrustpilotStats,
} from "domain/reviews/types";
import { getIntlLocale, useLocale, useTranslations } from "lib/i18n";
import {
  CATEGORY_RANKINGS,
  TRUSTPILOT_REVIEW_PAGE_URL,
} from "domain/reviews/constants";
import ReviewCard from "./ReviewCard";
import RankingBadge from "./RankingBadge";

interface Props {
  stats: TrustpilotStats | null;
  reviews: TrustpilotReview[];
  rankings?: TrustpilotCategoryRanking[];
}

const STARS_RATIO = 512 / 96;
const LOGO_RATIO = 1132.8 / 278.2;

const StarStrip: React.FC<{ height?: number }> = ({ height = 28 }) => (
  <TrustpilotStars5
    aria-hidden="true"
    width={Math.round(height * STARS_RATIO)}
    height={height}
    css={`
      display: inline-block;
      vertical-align: middle;
    `}
  />
);

const RatingHeader: React.FC<{ stats: TrustpilotStats }> = ({ stats }) => {
  const t = useTranslations("common");
  const locale = useLocale();
  const score = stats.trustScore.toFixed(1);
  const total = stats.totalReviews.toLocaleString(getIntlLocale(locale));
  return (
    <div
      aria-label={t("trustpilot.ratingAria", { score, total })}
      css={`
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 12px;
        margin-bottom: 32px;
      `}
    >
      <StarStrip height={28} />
      <div
        css={`
          font-size: 18px;
          color: #374151;
        `}
      >
        <strong
          css={`
            color: #111827;
            font-size: 22px;
            margin-right: 6px;
          `}
        >
          {score}
        </strong>
        {t("trustpilot.ratingSummary")}{" "}
        <strong
          css={`
            color: #111827;
          `}
        >
          {total}
        </strong>{" "}
        {t("trustpilot.reviewsLabel")}
      </div>
    </div>
  );
};

const TrustpilotSection: React.FC<Props> = ({
  stats,
  reviews,
  rankings = CATEGORY_RANKINGS,
}) => {
  const t = useTranslations("common");
  const { breakpoints } = useTheme();
  return (
    <section
      aria-labelledby="trustpilot-heading"
      css={`
        padding: 80px 20px;
        background-color: #ffffff;
        ${breakpoints.down("sm")} {
          padding: 60px 16px;
        }
      `}
    >
      <div
        css={`
          margin: 0 auto;
          max-width: 1020px;
          display: flex;
          flex-direction: column;
          align-items: center;
        `}
      >
        <h2
          id="trustpilot-heading"
          css={`
            margin: 0 0 24px;
            font-size: 32px;
            font-weight: 700;
            line-height: 1.2;
            text-align: center;
            color: #111827;
            ${breakpoints.down("sm")} {
              font-size: 26px;
            }
          `}
        >
          {t("trustpilot.heading")}
        </h2>

        {stats && <RatingHeader stats={stats} />}

        <div
          css={`
            width: 100%;
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 16px;
            margin-bottom: 20px;
            ${breakpoints.down("sm")} {
              grid-template-columns: 1fr;
            }
          `}
        >
          {rankings.map((ranking) => (
            <RankingBadge key={ranking.categorySlug} ranking={ranking} />
          ))}
        </div>

        {reviews.length > 0 && (
          <div
            css={`
              width: 100%;
              display: grid;
              grid-template-columns: repeat(3, 1fr);
              gap: 20px;
              margin-bottom: 32px;
              ${breakpoints.down("md")} {
                grid-template-columns: 1fr;
              }
            `}
          >
            {reviews.map((review) => (
              <ReviewCard key={review.id} review={review} />
            ))}
          </div>
        )}

        <a
          href={TRUSTPILOT_REVIEW_PAGE_URL}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={t("trustpilot.seeMoreAria")}
          css={`
            display: inline-flex;
            align-items: center;
            gap: 10px;
            font-size: 14px;
            font-weight: 500;
            color: #374151;
            text-decoration: none;
            :hover,
            :focus-visible {
              color: #111827;
              text-decoration: underline;
              text-underline-offset: 4px;
            }
          `}
        >
          {t("trustpilot.seeMore")}
          <TrustpilotLogo
            aria-hidden="true"
            width={Math.round(22 * LOGO_RATIO)}
            height={22}
            css={`
              display: inline-block;
              vertical-align: middle;
            `}
          />
        </a>
      </div>
    </section>
  );
};

export default TrustpilotSection;
